import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import { apiClient } from '@/shared/api/apiClient'
import { buildCraRiskAssessmentPdfFilename } from '@/shared/utils/pdf-export-filenames'

export type CraManufacturerBlock = {
  name: string
  address: string
  contactName: string
  contactDetails: string
}

export type CraStudio = {
  id: string
  productId: string
  productName: string
  productModel: string
  manufacturer: CraManufacturerBlock | null
  classification: string | null
  sections: ComplianceDocumentSectionRecord[] | null
  generatedAt: string | null
  hasIngestedSources: boolean
  contentLanguage: string
}

export type CraAnnexIIRequirement = {
  id: string
  number: string
  text: string
  children?: CraAnnexIIRequirement[]
}

export const CRA_STUDIO_QUERY_KEY = 'cra-studio'

export function useGetCraUserInformation(studioId: string) {
  return useQuery({
    queryKey: [CRA_STUDIO_QUERY_KEY, studioId, 'user-information'],
    queryFn: async () => {
      const response = await apiClient.get<{
        requirements: CraAnnexIIRequirement[]
      }>(`/cra-studio/${studioId}/user-information`)
      return response.data.requirements
    },
    enabled: studioId.length > 0,
  })
}

export function useGetCraStudio(studioId: string) {
  return useQuery({
    queryKey: [CRA_STUDIO_QUERY_KEY, studioId],
    queryFn: async () => {
      const response = await apiClient.get<CraStudio>(`/cra-studio/${studioId}`)
      return response.data
    },
    enabled: studioId.length > 0,
  })
}

export function useCreateCraStudio(productId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<CraStudio>(
        `/products/${productId}/cra-studio`
      )
      return response.data
    },
  })
}

export function useSaveCraRiskAssessment(studioId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (sections: ComplianceDocumentSectionRecord[]) => {
      const response = await apiClient.patch<CraStudio>(
        `/cra-studio/${studioId}/risk-assessment`,
        { sections }
      )
      return response.data
    },
    onSuccess: (studio) => {
      queryClient.setQueryData([CRA_STUDIO_QUERY_KEY, studioId], studio)
    },
  })
}

export type CraGenerateEvent =
  | { type: 'status'; phase: 'reading' | 'writing'; workflowRunId: string }
  | {
      type: 'section.started'
      section: string
      title: string
      workflowRunId: string
    }
  | { type: 'section.progress'; section: string; text: string }
  | {
      type: 'document.completed'
      sections: ComplianceDocumentSectionRecord[]
      generatedAt: string
      classification: string
      workflowRunId: string
      riskCount: number
      emptyReason: string | null
    }
  | { type: 'error'; error: string; workflowRunId?: string }

export async function generateCraRiskAssessment(params: {
  studioId: string
  language: 'en-GB' | 'de-DE'
  signal: AbortSignal
  onEvent: (event: CraGenerateEvent) => void
}) {
  const baseURL = apiClient.defaults.baseURL ?? ''
  const response = await fetch(
    `${baseURL}/cra-studio/${params.studioId}/risk-assessment/generate`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ language: params.language }),
      signal: params.signal,
    }
  )
  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as { message?: string }
      if (body.message) {
        message = body.message
      }
    } catch {
      // keep the status text
    }
    throw new Error(message)
  }
  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }
    buffer += decoder.decode(value, { stream: true })
    const chunks = buffer.split('\n\n')
    buffer = chunks.pop() ?? ''
    for (const chunk of chunks) {
      const line = chunk.split('\n').find((entry) => entry.startsWith('data: '))
      if (!line) {
        continue
      }
      const event = JSON.parse(line.slice('data: '.length)) as CraGenerateEvent
      params.onEvent(event)
    }
  }
}

export function useExportCraRiskAssessmentPdf(studioId: string) {
  return useMutation({
    mutationFn: async () => {
      const baseURL = apiClient.defaults.baseURL ?? ''
      const response = await fetch(
        `${baseURL}/cra-studio/${studioId}/risk-assessment/export-pdf`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...(apiClient.defaults.headers.common as Record<string, string>),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      )
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No risk assessment document found to export')
        }
        throw new Error(`Export failed: ${response.statusText}`)
      }
      const blob = await response.blob()
      const downloadUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = buildCraRiskAssessmentPdfFilename(new Date())
      link.click()
      URL.revokeObjectURL(downloadUrl)
    },
  })
}
